import CircularGraph from "../common/CircularGraph";
import { getCategoryColor } from "../../store/helper";
import { useDispatch, useSelector } from "react-redux";
import { platformActions, RootStatePlatform } from "../../store/platform-slice";
import useTheme from "../../../../common/hooks/useTheme";
import React, { CSSProperties, useState } from "react";
import { metricDataType } from "../../../../providers/dataPlatformReportTypes";
import { Theme } from "../../../../providers/ThemeProvider";

type Props = {
  item: metricDataType;
  categories: any;
  withShadow?: boolean;
  isClickable?: boolean;
  systemMetric?: number;
};

const MetricCard = ({
  item,
  categories,
  withShadow = true,
  isClickable = true,
  systemMetric,
}: Props) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const dispatch = useDispatch();
  let categoryColor = getCategoryColor(
    item.unit,
    item.score,
    theme,
    item.scoreCategory
  );
  const selectedMetric = useSelector(
    (state: RootStatePlatform) => state.platform.selectedMetric
  );
  const reportData = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.score
  );

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        if (isClickable) {
          dispatch(platformActions.setSelectedSystem(systemMetric));
          dispatch(platformActions.setSelectedMetric(item.id));
        }
      }}
      style={
        styles.cardContainer(
          theme,
          withShadow,
          isClickable,
          isHovered,
          selectedMetric === item.id
        ) as CSSProperties
      }
    >
      <div style={styles.labelContainer}>
        <div style={styles.label(theme)}>{item.label}</div>
        <div style={styles.category(theme)}>
          {item.id === "8_RMR" || item.id === "8_AMR" ? (
            <div>{item.score === 10 ? "Low" : "High"}</div>
          ) : (
            categories[item.scoreCategory]
          )}
        </div>
        {item.unit !== "%" && (
          <div style={styles.valueContainer(theme)}>
            {item.value}
            <span style={styles.unit}>{` ${item.unit}`}</span>
          </div>
        )}
      </div>

      <div style={styles.details}>
        {item.id === "8_RMR" || item.id === "8_AMR" ? (
          <div style={styles.scoreContainer}>
            <div style={styles.riskIconContainer(theme)}>
              <div
                style={styles.riskIconBar(
                  item.score === 10 ? "28px" : "10px",
                  theme.colorTokens.D[50]
                )}
              />
              <div
                style={styles.riskIconBar(
                  item.score === 100 ? "28px" : "10px",
                  theme.colorTokens.S[50]
                )}
              />
            </div>
          </div>
        ) : (
          <CircularGraph
            size={42}
            strokeWidth={3.5}
            value={item.score}
            color={categoryColor}
          />
        )}
      </div>
    </div>
  );
};

export default MetricCard;

const styles = {
  cardContainer: (
    theme: Theme,
    hasShadow: boolean,
    isClickable: boolean,
    isHovered: boolean,
    isSelected: boolean
  ) => ({
    // width: "100%",
    padding: "8px 12px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    borderRadius: 8,
    position: "relative",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    boxShadow: hasShadow ? "var(--box-shadow-general)" : "none",
    borderColor: isClickable
      ? isSelected
        ? theme.colors_ds.PRIMARY_ICON_MAIN
        : isHovered
        ? theme.colors_ds.NEUTRAL_BORDER_DEFAULT
        : theme.colors_ds.NEUTRAL_BORDER_WEAK
      : theme.colors_ds.NEUTRAL_BORDER_WEAK,
    cursor: isClickable ? "pointer" : "default",
  }),
  labelContainer: {
    maxWidth: "calc(100% - 42px)",
    overflow: "hidden",
  },
  label: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "14px",
  }),
  details: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  category: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  scoreContainer: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  value: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Bold)",
    fontSize: "14px",
  }),
  riskIconContainer: (theme: Theme) => ({
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    gap: "4px",
    width: "42px",
    height: "42px",
    padding: "4px",
    borderRadius: "26px",
    border: `3.5px solid ${theme.colorTokens.N[40]}`,
  }),
  riskIconBar: (height: string, color: string) => ({
    width: "5px",
    height: height,
    borderRadius: "50px",
    backgroundColor: color,
  }),
  valueContainer: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
  }),
  unit: {
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
  },
};
